/**
 * Modal URL Error Handling and Validation
 * 
 * Provides comprehensive error handling, validation, and debugging utilities
 * for the modal URL system. Helps catch configuration issues and invalid URLs
 * during development and provides helpful error messages.
 */

"use client";

import { useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { 
  isModalConfigured, 
  getModalConfig,
  type ConfiguredModalId 
} from "./modal-url-config";
import { parseModalUrl, validateModalParams } from "./modal-url-builder";

/**
 * Error types for modal URL operations
 */
export enum ModalUrlErrorType {
  MODAL_NOT_CONFIGURED = "MODAL_NOT_CONFIGURED",
  MISSING_ENTITY_ID = "MISSING_ENTITY_ID",
  INVALID_PARAMETERS = "INVALID_PARAMETERS",
  MALFORMED_URL = "MALFORMED_URL",
}

/**
 * Error class for modal URL operations
 */
export class ModalUrlError extends Error {
  constructor(
    public type: ModalUrlErrorType,
    message: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = "ModalUrlError";
  }
}

/**
 * Validate that a modal is properly configured
 * 
 * @throws {ModalUrlError} If modal is not configured
 */
export function assertModalConfigured(modalId: string): asserts modalId is ConfiguredModalId {
  if (!isModalConfigured(modalId)) {
    throw new ModalUrlError(
      ModalUrlErrorType.MODAL_NOT_CONFIGURED,
      `Modal "${modalId}" is not configured in MODAL_URL_CONFIG`,
      { modalId }
    );
  }
}

/**
 * Validate modal URL parameters
 * 
 * @throws {ModalUrlError} If parameters are invalid
 */
export function assertValidModalParams(
  modalId: string,
  params: Record<string, string | undefined>
): void {
  assertModalConfigured(modalId);
  
  const validation = validateModalParams(modalId, params);
  
  if (!validation.valid) {
    throw new ModalUrlError(
      ModalUrlErrorType.INVALID_PARAMETERS,
      `Invalid parameters for modal "${modalId}": ${validation.errors.join(", ")}`,
      { modalId, params, errors: validation.errors }
    );
  }
}

/**
 * Safe version of getModalConfig that returns null instead of throwing
 */
export function getModalConfigSafe(modalId: string) {
  try {
    return getModalConfig(modalId);
  } catch {
    return null;
  }
}

/**
 * Hook to validate modal URL on mount (development only)
 * 
 * Logs warnings when modal URLs are malformed or missing required parameters.
 * Only runs in development mode.
 * 
 * @example
 * ```tsx
 * function DealDetailModal() {
 *   useModalUrlValidation('deals-detail');
 *   // ... modal content
 * }
 * ```
 */
export function useModalUrlValidation(modalId: string) {
  const searchParams = useSearchParams();
  
  useEffect(() => {
    // Only run in development
    if (process.env.NODE_ENV !== "development") return;
    
    const currentModal = searchParams?.get("modal");
    
    // Only validate if this modal is active
    if (currentModal !== modalId) return;
    
    try {
      // Check if modal is configured
      assertModalConfigured(modalId);
      
      // Parse and validate parameters
      const parsed = parseModalUrl(searchParams, modalId);
      if (!parsed) {
        console.warn(`[ModalURL] Modal "${modalId}" is in URL but parameters could not be parsed`);
        return;
      }
      
      // Validate parameters
      const validation = validateModalParams(modalId, parsed.params);
      if (!validation.valid) {
        console.warn(
          `[ModalURL] Invalid parameters for modal "${modalId}":`,
          validation.errors
        );
      }
    } catch (error) {
      if (error instanceof ModalUrlError) {
        console.warn(`[ModalURL] ${error.message}`, error.details);
      }
    }
  }, [modalId, searchParams]);
}

/**
 * Development helper to log modal URL state
 */
export function debugModalUrl(searchParams: URLSearchParams | null | undefined) {
  if (process.env.NODE_ENV !== "development") return;
  
  const parsed = parseModalUrl(searchParams);
  
  if (!parsed) {
    console.log("[ModalURL Debug] No modal in URL");
    return;
  }
  
  console.group("[ModalURL Debug]");
  console.log("Modal ID:", parsed.modalId);
  console.log("Is Configured:", isModalConfigured(parsed.modalId));
  
  if (isModalConfigured(parsed.modalId)) {
    const config = getModalConfig(parsed.modalId);
    console.log("Config:", config);
    
    const validation = validateModalParams(parsed.modalId, parsed.params);
    console.log("Validation:", validation);
  }
  
  console.log("Parameters:", parsed.params);
  console.groupEnd();
}

/**
 * Hook to debug modal URL (development only)
 * 
 * Logs modal URL state whenever it changes during development
 */
export function useDebugModalUrl() {
  const searchParams = useSearchParams();
  
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    debugModalUrl(searchParams);
  }, [searchParams]);
}

/**
 * Error boundary helper for modal errors
 */
export function handleModalError(error: unknown): void {
  if (error instanceof ModalUrlError) {
    console.error(`[ModalURL Error] ${error.type}:`, error.message, error.details);
    
    // In development, show more detailed error
    if (process.env.NODE_ENV === "development") {
      console.error("Stack:", error.stack);
    }
  } else {
    console.error("[ModalURL] Unexpected error:", error);
  }
}

/**
 * Type guard for ModalUrlError
 */
export function isModalUrlError(error: unknown): error is ModalUrlError {
  return error instanceof ModalUrlError;
}

/**
 * Check if URL parameters are sufficient for a modal
 * 
 * @param modalId - Modal ID to check
 * @param searchParams - URL search parameters
 * @returns Object indicating if parameters are sufficient and any issues
 */
export function checkModalUrlSufficiency(
  modalId: string,
  searchParams: URLSearchParams | null | undefined
) {
  const result = {
    sufficient: false,
    issues: [] as string[],
  };
  
  if (!searchParams) {
    result.issues.push("No search parameters provided");
    return result;
  }
  
  // Check if modal is in URL
  const currentModal = searchParams.get("modal");
  if (currentModal !== modalId) {
    result.issues.push(`Modal parameter is "${currentModal}", expected "${modalId}"`);
    return result;
  }
  
  // Check if modal is configured
  if (!isModalConfigured(modalId)) {
    result.issues.push(`Modal "${modalId}" is not configured`);
    return result;
  }
  
  // Parse parameters
  const parsed = parseModalUrl(searchParams, modalId);
  if (!parsed) {
    result.issues.push("Could not parse modal parameters from URL");
    return result;
  }
  
  // Validate parameters
  const validation = validateModalParams(modalId, parsed.params);
  if (!validation.valid) {
    result.issues.push(...validation.errors);
    return result;
  }
  
  result.sufficient = true;
  return result;
}

/**
 * Hook to check modal URL sufficiency and provide warnings
 */
export function useModalUrlCheck(modalId: string) {
  const searchParams = useSearchParams();
  
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    
    const currentModal = searchParams?.get("modal");
    if (currentModal !== modalId) return;
    
    const check = checkModalUrlSufficiency(modalId, searchParams);
    
    if (!check.sufficient) {
      console.warn(
        `[ModalURL] Modal "${modalId}" URL may be insufficient:`,
        check.issues
      );
    }
  }, [modalId, searchParams]);
}
